// @flow

import React from 'react';
import { connect } from 'react-redux';
import AttributionAvatar from 'app/common/react/components/PinRep/components/Footer/Attribution/AttributionAvatar';
import AttributionName from 'app/common/react/components/PinRep/components/Footer/Attribution/AttributionName';
import AttributionTitle from 'app/common/react/components/PinRep/components/Footer/Attribution/AttributionTitle';
import AvatarHoverCard from 'app/common/react/components/AvatarHoverCard/AvatarHoverCard';
import getAttributionProps from 'app/common/react/components/PinRep/utils/getAttributionProps';
import getPinTypes from 'app/common/react/components/PinRep/utils/getPinTypes';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import useTrackingCallback from 'app/common/react/components/PinRep/Hooks/useTrackingCallback';
import { Box } from 'gestalt';
import { setMostRecentPin } from 'app/common/redux/actions/mostRecentPin';
import { type Action } from 'app/common/redux/actions/types';
import { type Dispatch } from 'redux';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { type State as ReduxState } from 'app/common/redux/types';
import { useTrackingParameters } from 'app/common/react/components/PinRep/TrackingParametersContext';
import {
  type FeedbackData,
  type FeedbackType,
} from 'app/common/react/components/PinRep/feedback-types';

const AVATAR_SIZE_PIXELS = 32;

type ReduxDispatchProps = {|
  setMostRecentPin: (pin_id: string) => void,
|};

type OwnProps = {|
  pin: Pin,

  feedbackData: FeedbackData,

  // Indicates why a pin appears in a given grid
  feedbackType: FeedbackType,

  // Determines whether a promoted pin is from a user with an account. Used to determine whether or not to show AvatarHoverCard.
  isDarkPromoter?: boolean,

  // The title for the pin (uses more args than just the pin, so determined higher up)
  pinTitle: string,

  // Used to force a hover experience. See PinRepContainer for more context.
  setForcePrimary: (boolean) => void,
|};

type Props = {|
  ...ReduxDispatchProps,
  ...OwnProps,
|};

export function Attribution({
  feedbackData,
  feedbackType,
  isDarkPromoter,
  pin,
  pinTitle,
  setForcePrimary,
  setMostRecentPin,
}: Props) {
  const { id: pinId, pinner } = pin;
  const { isPromoted } = getPinTypes(pin);
  const user = pinner || pin.promoter;

  const trackClick = useTrackingCallback(PCONST.thrift.EventType.CLICK, {
    element: isPromoted
      ? PCONST.thrift.ElementType.PIN_CELL_BUTTON_PROMOTED_PIN_SOURCE
      : PCONST.thrift.ElementType.PIN_ATTRIBUTION,
    objectId: pinId,
  });

  const { avatarUrl, attributionNameLink, attributionTitle, name, url } = getAttributionProps(pin);

  const { viewParameter, viewType } = useTrackingParameters();

  const wrapInLink = url && !attributionNameLink;

  const content = (
    <Box
      alignItems="center"
      display="flex"
      dangerouslySetInlineStyle={{ __style: { marginLeft: '-3px', marginRight: '-3px' } }}
    >
      <Box
        flex="none"
        dangerouslySetInlineStyle={{ __style: { marginLeft: '3px', marginRight: '3px' } }}
      >
        <AvatarHoverCard
          avatarSize={AVATAR_SIZE_PIXELS}
          isDarkPromoter={isDarkPromoter}
          pinId={pinId}
          user={user}
          username={user && user.username}
          viewParameter={viewParameter}
          viewType={viewType}
        >
          <AttributionAvatar
            attributionNameLink={attributionNameLink}
            avatarUrl={avatarUrl}
            name={name}
          />
        </AvatarHoverCard>
      </Box>
      <Box
        dangerouslySetInlineStyle={{ __style: { marginLeft: '3px', marginRight: '3px' } }}
        flex="grow"
        overflow="hidden"
      >
        <Box dangerouslySetInlineStyle={{ __style: { marginBottom: '2px' } }}>
          <AttributionName
            attributionNameLink={attributionNameLink}
            name={name}
            regularWeight={!attributionTitle}
          />
        </Box>
        <AttributionTitle title={attributionTitle} />
      </Box>
    </Box>
  );

  return wrapInLink ? (
    <NavigateLink
      className="underlineLink"
      onClick={() => {
        setMostRecentPin(pinId);
        trackClick();
      }}
      to={url}
    >
      {content}
    </NavigateLink>
  ) : (
    content
  );
}

function mapDispatchToProps(dispatch: Dispatch<Action>): ReduxDispatchProps {
  return {
    setMostRecentPin: (pinId) => {
      dispatch(setMostRecentPin(pinId));
    },
  };
}

export default connect<Props, OwnProps, _, ReduxDispatchProps, ReduxState, Dispatch<Action>>(
  null,
  mapDispatchToProps,
)(Attribution);
